import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { createInquiry } from '../api/action/inquiry.jsx';

const fieldNameFromLabel = (label = '', type = '') => {
  const normalized = `${label} ${type}`.toLowerCase();
  if (normalized.includes('last')) return 'lastName';
  if (normalized.includes('email')) return 'email';
  if (normalized.includes('phone') || normalized.includes('mobile')) return 'phoneNumber';
  if (normalized.includes('message') || normalized.includes('query') || normalized.includes('comment')) return 'query';
  if (normalized.includes('company') || normalized.includes('organization')) return 'companyName';
  return 'firstName';
};

const formatCountryCode = (countryCode) => {
  if (!countryCode) return '';
  const value = String(countryCode).trim();
  return value.startsWith('+') ? value : `+${value}`;
};

const formatPhone = (contactPerson = {}) => {
  if (!contactPerson.phoneNumber) return '';
  return [formatCountryCode(contactPerson.countryCode), contactPerson.phoneNumber].filter(Boolean).join(' ');
};

const formatAddress = (address = {}) => {
  return [
    address.streetName1,
    address.streetName2,
    [address.city, address.state].filter(Boolean).join(', '),
    [address.country, address.zipcode].filter(Boolean).join(' ')
  ].filter(Boolean).join('\n');
};

const isEmail = (value = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const normalizeCompanyBaseUrl = (company = {}) => {
  const rawUrl = String(company.url || company.companyUrl || '').trim();
  const baseUrl = rawUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!baseUrl) return '';
  const withProtocol = /^https?:\/\//i.test(baseUrl) || baseUrl.startsWith('/')
    ? baseUrl
    : `https://${baseUrl}`;
  return withProtocol.replace(/\/+$/, '');
};

export const getCompanyContactUrl = (company = {}) => {
  const baseUrl = normalizeCompanyBaseUrl(company);
  return baseUrl ? `${baseUrl}/contact` : '/contact';
};

export const buildInquiryNotificationFields = (company = {}) => {
  const companyName = company?.name || company?.companyName || 'Company';
  const companyUrl = getCompanyContactUrl(company);

  return {
    notificationInfo: {
      notifiy: true,
      channels: ['EMAIL'],
      messageType: 'CONTACT_INQUIRY'
    },
    attributes: [
      { value: companyName, key: 'COMPANY_NAME' },
      { value: companyUrl, key: 'COMPANY_URL' }
    ]
  };
};

export const useTemplateContactInfo = (content = {}) => {
  const company = useSelector((state) => state.companyReducer?.company || state.company?.companyDetails || {});

  return useMemo(() => {
    const contactPerson = company?.contactPerson || {};
    const address = contactPerson.addressList?.[0] || company?.addressList?.[0] || {};
    const contactName = [contactPerson.firstName, contactPerson.lastName].filter(Boolean).join(' ');

    return {
      company,
      contactPerson,
      contactName,
      phoneValue: formatPhone(contactPerson) || content.phoneValue || content.phone || '',
      emailValue: contactPerson.email || content.emailValue || content.email || '',
      addressValue: formatAddress(address) || content.addressValue || content.address || ''
    };
  }, [company, content]);
};

export const useTemplateInquiryForm = (fields = []) => {
  const company = useSelector((state) => state.companyReducer?.company || state.company?.companyDetails || {});
  const [values, setValues] = useState({ firstName: '', lastName: '', email: '', phoneNumber: '', query: '', companyName: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const fieldNames = useMemo(() => fields.map((field) => fieldNameFromLabel(field.label, field.type)), [fields]);
  const isFieldRequired = (name) => fields.some((field) => fieldNameFromLabel(field.label, field.type) === name && field.required !== false);

  const setFieldValue = (name, value) => {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setStatus(null);
  };

  const validate = () => {
    const nextErrors = {};
    if (fieldNames.includes('firstName') && (isFieldRequired('firstName') || values.firstName.trim()) && values.firstName.trim().length < 2) {
      nextErrors.firstName = 'Enter a valid first name.';
    }
    if (fieldNames.includes('lastName') && (isFieldRequired('lastName') || values.lastName.trim()) && values.lastName.trim().length < 2) {
      nextErrors.lastName = 'Enter a valid last name.';
    }
    if (fieldNames.includes('email') && (isFieldRequired('email') || values.email.trim()) && !isEmail(values.email)) {
      nextErrors.email = 'Enter a valid email.';
    }
    if (fieldNames.includes('phoneNumber') && (isFieldRequired('phoneNumber') || values.phoneNumber.trim()) && values.phoneNumber.replace(/\D/g, '').length < 7) {
      nextErrors.phoneNumber = 'Enter a valid phone number.';
    }
    if (fieldNames.includes('query') && isFieldRequired('query') && !values.query.trim()) {
      nextErrors.query = 'Enter a message.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitInquiry = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setStatus(null);
    const operatingLocation = company?.operatingLocation?.code;
    const countryCode = operatingLocation === 'IND' ? '+91' : operatingLocation === 'US' ? '+1' : '';
    const inquiryData = {
      user: {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        phoneNumber: values.phoneNumber.replace(/\D/g, ''),
        countryCode,
        contactPersonList: []
      },
      query: [values.query.trim(), values.companyName?.trim() ? `Company: ${values.companyName.trim()}` : ''].filter(Boolean).join('\n\n'),
      company: {
        primaryId: company?.primaryId,
        name: company?.name,
        url: company?.url,
        domain: { type: company?.domain?.type || company?.domainType || 'INSTITUTION' }
      },
      ...buildInquiryNotificationFields(company)
    };

    try {
      const result = await createInquiry(inquiryData);
      localStorage.setItem('inquiryData', JSON.stringify(result));
      setValues({ firstName: '', lastName: '', email: '', phoneNumber: '', query: '', companyName: '' });
      setStatus({ type: 'success', message: 'Thanks for reaching out. We will be in touch soon.' });
    } catch (error) {
      setStatus({ type: 'error', message: 'Error sending enquiry. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return {
    values,
    errors,
    status,
    submitting,
    fieldNameFromLabel,
    setFieldValue,
    submitInquiry
  };
};
